// Review Inbox — the operational workspace where a reviewer decides on additional contributions
// and indicator values. Which items appear and what each decision sends to the API are unchanged:
// the caller passes the same permission predicates and API functions the previous inbox used.
import { useMemo, useState } from "react";
import UiIcon from "../components/UiIcon";
import { formatNumber } from "./copy";
import { formatShortDate, formatStamp, statusLabel, workspaceCopy } from "./workspaceCopy";
import {
    MANAGED, SectionHeader, SummaryCounters, FilterTabs, WsEmpty, GroupLabel, ReviewItem, DetailDrawer, DrawerSection,
    MetaList, RecordHistory, Avatar,
} from "./WorkspaceUi";
import ContributionTimeline from "../components/contributions/ContributionTimeline";
import { FeedbackCard } from "../components/contributions/ContributionDetail";
import { contributionCopy } from "../components/contributions/contributionCopy";
import { buildWorkflow } from "../components/contributions/contributionWorkflow";

const ISO = /^\d{4}-\d{2}-\d{2}/;
const iso = value => (value && ISO.test(String(value)) ? value : null);
const latest = (...values) => values.map(iso).filter(Boolean).sort().pop() || null;

const ACTIVITY_REVIEWABLE = ["pending_review", "resubmitted"];
const DECIDED = ["approved", "rejected"];

export function buildInboxItems({ activities = [], indicatorData = [], indicatorDefs = [], users = [], activityGroups = [], periods = [], activePeriod, evaluatorId, canReviewActivity }) {
    const nameOf = id => users.find(u => u.id === id)?.name || null;
    const periodLabel = id => periods.find(p => p.id === id)?.label || (activePeriod?.id === id ? activePeriod.label : null);
    const items = [];
    for (const a of activities) {
        if (!canReviewActivity(a)) continue;
        const status = String(a.status || "").toLowerCase();
        const bucket = ACTIVITY_REVIEWABLE.includes(status) ? "needs_review" : status === "revision_requested" ? "waiting" : DECIDED.includes(status) ? "completed" : null;
        if (!bucket) continue;
        const group = activityGroups.find(g => g.id === a.groupId)?.label;
        items.push({
            key: `activity-${a.id}`, kind: "activity", id: a.id, bucket, status, raw: a,
            title: a.title, personId: a.userId, person: nameOf(a.userId),
            category: [group, a.sub].filter(Boolean).join(" · ") || null, cycle: periodLabel(a.periodId),
            context: [group, periodLabel(a.periodId)].filter(Boolean).join(" · "),
            submittedAt: iso(a.resubmittedAt) || iso(a.submittedAt),
            decidedAt: latest(a.approvedAt, a.rejectedAt, a.revisionRequestedAt),
            resubmitted: status === "resubmitted",
            attachment: Boolean(a.doc || a.attachments?.length),
        });
    }
    for (const d of indicatorData) {
        const def = indicatorDefs.find(i => i.id === d.indicatorId);
        if (!def || def.approverId !== evaluatorId) continue;
        const status = String(d.status || "").toLowerCase();
        const bucket = status === "submitted" && d.periodId === activePeriod?.id ? "needs_review"
            : status === "revision_requested" ? "waiting" : DECIDED.includes(status) ? "completed" : null;
        if (!bucket) continue;
        const reversed = (d.history || []).slice().reverse();
        const decidedEvent = reversed.find(e => ["approved", "rejected", "revision_requested"].includes(String(e.toStatus || e.type || "").toLowerCase()));
        items.push({
            key: `indicator-${d.id}`, kind: "indicator", id: d.id, bucket, status, raw: d, def,
            title: [def.label, d.scheduleSnapshot?.label].filter(Boolean).join(" — "), personId: def.registrarId, person: nameOf(def.registrarId),
            context: periodLabel(d.periodId), cycle: periodLabel(d.periodId),
            submittedAt: iso(d.submittedAt),
            decidedAt: latest(d.approvedAt, decidedEvent?.createdAt),
            dueAt: iso(d.scheduleSnapshot?.approvalDeadline),
            value: d.actual, unit: def.unit, target: d.target,
        });
    }
    return items;
}

const BUCKET_ORDER = ["needs_review", "waiting", "completed"];
function sortItems(items) {
    const at = item => (item.bucket === "needs_review" ? item.submittedAt : item.decidedAt || item.submittedAt) || "";
    return [...items].sort((a, b) => BUCKET_ORDER.indexOf(a.bucket) - BUCKET_ORDER.indexOf(b.bucket)
        // Oldest submissions first (they have waited longest); decided items newest first.
        || (a.bucket === "needs_review" ? at(a).localeCompare(at(b)) : at(b).localeCompare(at(a)))
        || String(a.title).localeCompare(String(b.title)));
}

export function ReviewInbox({ language, activities, indicatorData, indicatorDefs, users, activityGroups, periods, activePeriod, evaluator,
    impactLevels = [], canReviewActivity, reviewActivity, reviewIndicator, notify }) {
    const t = workspaceCopy(language);
    const [statusFilter, setStatusFilter] = useState("needs_review");
    const [typeFilter, setTypeFilter] = useState("all");
    const [openKey, setOpenKey] = useState(null);

    const items = useMemo(() => sortItems(buildInboxItems({
        activities, indicatorData, indicatorDefs, users, activityGroups, periods, activePeriod, evaluatorId: evaluator.id, canReviewActivity,
    })), [activities, indicatorData, indicatorDefs, users, activityGroups, periods, activePeriod, evaluator.id, canReviewActivity]);

    const counts = BUCKET_ORDER.reduce((acc, bucket) => ({ ...acc, [bucket]: items.filter(i => i.bucket === bucket).length }), {});
    const kinds = new Set(items.map(i => i.kind));
    const byType = typeFilter === "all" ? items : items.filter(i => i.kind === typeFilter);
    const shown = statusFilter === "all" ? byType : byType.filter(i => i.bucket === statusFilter);
    const open = items.find(i => i.key === openKey) || null;

    const dateLine = item => {
        if (item.bucket === "needs_review") {
            if (item.dueAt) return t.inbox.dueOn(formatShortDate(item.dueAt, language));
            return item.submittedAt ? t.inbox.submittedOn(formatShortDate(item.submittedAt, language)) : null;
        }
        const when = item.decidedAt || item.submittedAt;
        if (!when) return null;
        return item.bucket === "waiting" ? t.inbox.returnedOn(formatShortDate(when, language)) : t.inbox.decidedOn(formatShortDate(when, language));
    };
    // Indicators expose their reported value at a glance; contributions are judged on their description.
    const summaryOf = item => (item.kind === "indicator" && item.value != null
        ? `${formatNumber(item.value, language)}${item.unit ? ` ${item.unit}` : ""}${item.target != null ? ` / ${formatNumber(item.target, language)}` : ""}`
        : null);
    const statusFor = item => (item.bucket === "completed" ? item.status : item.bucket === "waiting" ? "waiting" : "needs_review");
    const statusText = item => (item.bucket === "completed" ? statusLabel(item.status, language) : item.bucket === "waiting" ? t.status.waiting : t.status.needs_review);

    const renderRow = item => <ReviewItem key={item.key} language={language}
        icon={item.kind === "indicator" ? "target" : "activity"} typeLabel={t.inbox.typeLabel[item.kind]}
        flag={item.resubmitted ? t.inbox.resubmitted : null}
        title={item.title} person={item.person} context={item.context}
        summary={summaryOf(item)} date={dateLine(item)}
        status={statusFor(item)} statusText={statusText(item)}
        cta={item.bucket === "needs_review" ? t.common.review : t.common.view} ctaPrimary={item.bucket === "needs_review"}
        onOpen={() => setOpenKey(item.key)} />;

    const empty = t.inbox.empty[statusFilter] || t.inbox.empty.all;
    return <div className="cvi-ws-page cvi-ws-inbox" {...MANAGED}>
        <SectionHeader title={t.inbox.title} subtitle={t.inbox.subtitle} />
        <SummaryCounters label={t.inbox.statusFilter} value={statusFilter} onChange={setStatusFilter} items={BUCKET_ORDER.map(bucket => ({
            id: bucket, label: t.inbox.counters[bucket], help: t.inbox.countersHelp[bucket], count: formatNumber(counts[bucket], language), zero: counts[bucket] === 0,
            tone: bucket === "needs_review" ? "attention" : "neutral",
        }))} />
        <div className="cvi-ws-toolbar">
            <FilterTabs label={t.inbox.statusFilter} value={statusFilter} onChange={setStatusFilter} options={[
                { id: "all", label: t.inbox.filters.all, count: items.length },
                ...BUCKET_ORDER.map(bucket => ({ id: bucket, label: t.inbox.filters[bucket], count: counts[bucket] })),
            ]} />
            {kinds.size > 1 && <FilterTabs size="sm" label={t.inbox.typeFilter} value={typeFilter} onChange={setTypeFilter} options={[
                { id: "all", label: t.inbox.types.all }, { id: "activity", label: t.inbox.types.activity }, { id: "indicator", label: t.inbox.types.indicator },
            ]} />}
        </div>
        <section className="cvi-ws-panel" aria-label={t.inbox.title}>
            {shown.length === 0 ? <WsEmpty title={empty[0]} description={empty[1]} tone={statusFilter === "needs_review" || statusFilter === "all" ? "calm" : "neutral"} icon={statusFilter === "needs_review" ? "check" : "inbox"} />
                : statusFilter === "all"
                    ? BUCKET_ORDER.filter(bucket => shown.some(i => i.bucket === bucket)).map(bucket => <div key={bucket}>
                        <GroupLabel count={formatNumber(shown.filter(i => i.bucket === bucket).length, language)}>{t.inbox.groups[bucket]}</GroupLabel>
                        <ul className="cvi-ws-review-list">{shown.filter(i => i.bucket === bucket).map(renderRow)}</ul>
                    </div>)
                    : <ul className="cvi-ws-review-list">{shown.map(renderRow)}</ul>}
        </section>
        {open && <ReviewDrawer key={open.key} item={open} language={language} onClose={() => setOpenKey(null)}
            users={users} evaluator={evaluator} impactLevels={impactLevels} activePeriod={activePeriod}
            reviewActivity={reviewActivity} reviewIndicator={reviewIndicator} notify={notify}
            statusFor={statusFor} statusText={statusText} />}
    </div>;
}

// ── Detail drawer with the decision form ───────────────────────────────────

function ReviewDrawer({ item, language, onClose, users, evaluator, impactLevels, reviewActivity, reviewIndicator, notify, statusFor, statusText }) {
    const t = workspaceCopy(language);
    const [impactLevel, setImpactLevel] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const nameOf = id => users.find(u => u.id === id)?.name || "—";
    const stamp = value => formatStamp(value, language);
    const isActivity = item.kind === "activity";
    const a = item.raw;
    const canDecide = item.bucket === "needs_review" && (isActivity || (item.def.approverId === evaluator.id && item.def.registrarId !== evaluator.id));
    const impactName = level => t.impact[level.id] || level.label;
    const selectedImpact = impactLevels.find(l => l.id === impactLevel);

    const submit = async decision => {
        if (saving) return;
        if (isActivity) {
            if (decision === "approve" && !impactLevel) return;
            if (["revision", "reject"].includes(decision) && !note.trim()) return;
        } else if (decision === "revision_requested" && !note.trim()) return;
        setSaving(true); setError("");
        try {
            if (isActivity) await reviewActivity(item.id, decision, impactLevel, note);
            else await reviewIndicator(item.id, decision, note);
            onClose();
        } catch (failure) {
            console.error("Review decision failed:", failure);
            const message = failure?.message || t.review.decisionFailed;
            setError(message);
            notify?.(message);
        } finally { setSaving(false); }
    };

    const meta = isActivity ? [
        { label: t.review.person, value: item.person && <span className="cvi-ws-inline-person"><Avatar name={item.person} size={20} />{item.person}</span> },
        { label: t.review.category, value: item.category },
        { label: t.review.cycle, value: item.cycle },
        { label: t.review.submitted, value: item.submittedAt ? stamp(item.submittedAt) : null },
        { label: t.review.impact, value: item.bucket === "completed" && a.impactLevel ? (t.impact[a.impactLevel] || impactLevels.find(l => l.id === a.impactLevel)?.label) : null },
        { label: t.review.score, value: item.bucket === "completed" && a.score ? formatNumber(a.score, language) : null },
    ] : [
        { label: t.review.registrar, value: item.person },
        { label: t.review.owner, value: item.def.ownerId ? nameOf(item.def.ownerId) : null },
        { label: t.review.cycle, value: item.cycle },
        { label: t.review.submitted, value: item.submittedAt ? stamp(item.submittedAt) : null },
        { label: t.review.approvalDue, value: item.dueAt ? formatShortDate(item.dueAt, language) : null },
    ];

    const footer = canDecide ? <>
        {isActivity && impactLevels.length > 0 && <fieldset className="cvi-ws-field">
            <legend>{t.review.impactRequired} <small>· {t.review.impactHelp}</small></legend>
            <div className="cvi-ws-choices">
                {impactLevels.map(level => <button key={level.id} type="button" className="cvi-ws-choice" aria-pressed={impactLevel === level.id} onClick={() => setImpactLevel(level.id)}>
                    <strong>{impactName(level)}</strong><small>{t.review.points(level.score)}</small>
                </button>)}
            </div>
        </fieldset>}
        <label className="cvi-ws-field">
            <span>{t.review.note} <small>· {isActivity ? t.review.noteHelp : t.review.noteIndicatorHelp}</small></span>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={t.review.notePlaceholder} />
        </label>
        {isActivity && selectedImpact && <p className="cvi-ws-muted">{t.review.scorePreview(t.review.points(selectedImpact.score))}</p>}
        {error && <p className="cvi-ws-error" role="alert">{error}</p>}
        <div className="cvi-ws-decision">
            {isActivity ? <>
                <button type="button" className="cvi-ws-btn is-danger" disabled={saving || !note.trim()} onClick={() => submit("reject")}>{t.review.reject}</button>
                <button type="button" className="cvi-ws-btn is-warning" disabled={saving || !note.trim()} onClick={() => submit("revision")}>{t.review.revise}</button>
                <button type="button" className="cvi-ws-btn is-primary" disabled={saving || !impactLevel} onClick={() => submit("approve")}>{saving ? t.review.saving : t.review.approve}</button>
            </> : <>
                <button type="button" className="cvi-ws-btn is-warning" disabled={saving || !note.trim()} onClick={() => submit("revision_requested")}>{t.review.revise}</button>
                <button type="button" className="cvi-ws-btn is-primary" disabled={saving} onClick={() => submit("approved")}>{saving ? t.review.saving : t.review.approve}</button>
            </>}
        </div>
    </> : null;

    const reviewerNote = isActivity ? a.managerNote : a.reviewNote;
    // Contributions use the shared approval timeline built from the recorded workflow history.
    const cc = isActivity ? contributionCopy(language) : null;
    const owner = isActivity ? users.find(u => u.id === a.userId) : null;
    const workflow = isActivity ? buildWorkflow(a, {
        ownerName: item.person, reviewerName: owner?.managerId ? users.find(u => u.id === owner.managerId)?.name || null : null,
        reviewerId: owner?.managerId || null, currentUserId: evaluator.id,
    }) : null;
    const awarded = isActivity && workflow.status === "approved" ? impactLevels.find(l => l.id === a.impactLevel) : null;
    return <DetailDrawer open onClose={onClose} language={language} width="lg"
        typeLabel={t.inbox.typeLabel[item.kind]} typeIcon={isActivity ? "activity" : "target"} title={item.title}
        status={statusFor(item)} statusText={statusText(item)}
        headerExtra={item.resubmitted ? <span className="cvi-ws-tag">{t.inbox.resubmitted}</span> : null}
        footer={footer}>
        <DrawerSection><MetaList items={meta} /></DrawerSection>
        {isActivity ? <DrawerSection title={t.review.description}>
            <p className="cvi-ws-drawer-text">{a.desc || t.review.noDescription}</p>
            {a.doc && <p className="cvi-ws-muted" style={{ marginTop: 10 }}><UiIcon name="paperclip" size={12} /> {a.doc}</p>}
        </DrawerSection> : <DrawerSection title={t.inbox.value}>
            <div className="cvi-ws-figure">
                <strong>{a.actual == null ? "—" : formatNumber(a.actual, language)}{item.def.unit ? ` ${item.def.unit}` : ""}</strong>
                <span>{a.target != null ? `${t.inbox.target}: ${formatNumber(a.target, language)}${item.def.unit ? ` ${item.def.unit}` : ""}` : t.inbox.noTarget}</span>
            </div>
            {a.doc && <p className="cvi-ws-muted" style={{ marginTop: 10 }}><UiIcon name="paperclip" size={12} /> {a.doc}</p>}
            {(a.deviationReason || a.correctiveAction) && <div className="cvi-ws-callout is-warning" style={{ marginTop: 12 }}><dl>
                {a.deviationReason && <div><dt>{t.review.deviation}</dt><dd>{a.deviationReason}</dd></div>}
                {a.correctiveAction && <div><dt>{t.review.corrective}</dt><dd>{a.correctiveAction}</dd></div>}
            </dl></div>}
        </DrawerSection>}
        {item.bucket === "waiting" && <DrawerSection><div className="cvi-ws-callout is-info">{t.review.waitingResubmission}</div></DrawerSection>}
        {isActivity ? <DrawerSection>
            <div className="cvi-contrib is-embedded" data-cvi-i18n="managed" lang={language}>
                {workflow.feedback && <FeedbackCard feedback={workflow.feedback} c={cc} language={language} />}
                <ContributionTimeline workflow={workflow} c={cc} language={language} headingLevel={3}
                    impact={awarded ? { label: impactName(awarded), points: awarded.score ?? a.score } : null} />
            </div>
        </DrawerSection> : <>
            {item.bucket !== "needs_review" && reviewerNote && <DrawerSection title={t.review.managerFeedback}>
                <p className="cvi-ws-drawer-text">{reviewerNote}</p>
            </DrawerSection>}
            <DrawerSection title={t.common.history}>
                <RecordHistory events={(a.history || []).length ? a.history : [{ type: "submitted", createdAt: item.submittedAt, actorName: item.person }]} language={language} formatTime={stamp} />
            </DrawerSection>
        </>}
    </DetailDrawer>;
}
